import { ActionIcon, Box, Button, Chip, Group, Modal, NumberInput, Select, Stack, Switch, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import type { LookupRow } from '../api/types';
import { tr } from '../lib/i18n';
import { matches } from '../lib/search';
import { bi, BilingualField, BilingualScope, BilingualToolbar, missingLang, useBilingualScope, type Bilingual } from '../ui/bilingual';
import { ClientTable, type Column } from '../ui/DataTable';
import { BilingualText, Code, ErrorState, Panel, SearchInput, Toolbar } from '../ui/kit';
import { useToast } from './Toast';

export interface ExtraField {
  key: string; // body key, e.g. categoryId
  column: keyof LookupRow;
  label: string;
  description?: string;
  options?: { value: string | number; label: string }[];
  required?: boolean;
  createOnly?: boolean;
  numeric?: boolean;
}

interface Props {
  path: string; // e.g. property-categories
  queryKey: string;
  extraFields?: ExtraField[];
  extraColumns?: { label: string; render: (r: LookupRow) => ReactNode; sortValue?: (r: LookupRow) => string | number | null | undefined; width?: number | string }[];
  filter?: ExtraField; // optional list filter (e.g. by category)
  rowActions?: (r: LookupRow) => ReactNode;
  addLabel?: string;
  lockedRow?: (r: LookupRow) => string | null; // reason why the row cannot be deactivated
  title?: string;
  description?: string;
}

type Draft = { id?: number; code: string; name: Bilingual; sortOrder: number; isActive: boolean; [k: string]: unknown };

/**
 * Generic CRUD table for master data.
 * Codes are immutable after creation; rows are deactivated, never deleted.
 */
export function LookupManager({ path, queryKey, extraFields = [], extraColumns = [], filter, rowActions, addLabel = tr('إضافة', 'Add'), lockedRow, title, description }: Props) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filterValue, setFilterValue] = useState<string>('');
  const [q, setQ] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [state, setState] = useState<'all' | 'active' | 'inactive'>('all');

  const list = useQuery({
    queryKey: [queryKey, filterValue],
    queryFn: () => api<{ items: LookupRow[] }>(`/${path}`, { query: { includeInactive: true, ...(filter && filterValue ? { [filter.key]: filterValue } : {}) } }),
  });

  useEffect(() => setErrors({}), [draft?.id]);

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body: Record<string, unknown> = { nameAr: d.name.ar.trim(), nameEn: d.name.en.trim(), sortOrder: Number(d.sortOrder) || 0, isActive: d.isActive };
      for (const f of extraFields) {
        if (d.id && f.createOnly) continue;
        const v = d[f.key];
        if (v !== undefined && v !== '' && v !== null) body[f.key] = f.numeric ? Number(v) : v;
        else if (!f.required) body[f.key] = null;
      }
      return d.id ? api(`/${path}/${d.id}`, { method: 'PATCH', body }) : api(`/${path}`, { method: 'POST', body: { ...body, code: d.code } });
    },
    onSuccess: () => {
      toast(tr('تم الحفظ', 'Saved'));
      setDraft(null);
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        const fe = e.fieldErrors();
        if (e.code === 'DUPLICATE') fe.code = e.message;
        setErrors(fe);
      }
      toastError(e);
    },
  });

  const toggle = useMutation({
    mutationFn: (r: LookupRow) => api(`/${path}/${r.id}`, { method: 'PATCH', body: { isActive: !r.is_active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
    onError: toastError,
  });

  const all = list.data?.items ?? [];
  const open = (r?: LookupRow) =>
    setDraft(
      r
        ? { id: r.id, code: r.code, name: bi(r.name_ar, r.name_en), sortOrder: r.sort_order, isActive: r.is_active, ...Object.fromEntries(extraFields.map((f) => [f.key, r[f.column] ?? ''])) }
        : { code: '', name: bi(), sortOrder: all.length + 1, isActive: true, ...Object.fromEntries(extraFields.map((f) => [f.key, filter?.key === f.key ? filterValue : ''])) },
    );

  const missingCount = all.filter((r) => !r.name_en?.trim()).length;
  const activeCount = all.filter((r) => r.is_active).length;
  const items = useMemo(
    () =>
      all
        .filter((r) => !onlyMissing || !r.name_en?.trim())
        .filter((r) => state === 'all' || (state === 'active') === r.is_active)
        .filter((r) => matches(q, r.name_ar, r.name_en, r.code)),
    [all, onlyMissing, state, q],
  );

  const columns: Column<LookupRow>[] = [
    { accessor: 'sort_order', title: '#', width: 56, sortable: true, render: (r) => <Text size="sm" c="dimmed" className="pf-num">{r.sort_order}</Text> },
    { accessor: 'name_ar', title: tr('الاسم', 'Name'), sortable: true, render: (r) => <BilingualText ar={r.name_ar} en={r.name_en} /> },
    { accessor: 'code', title: tr('الكود', 'Code'), sortable: true, render: (r) => <Code>{r.code}</Code> },
    ...extraColumns.map((c, i): Column<LookupRow> => ({ accessor: `extra${i}`, title: c.label, width: c.width, sortable: !!c.sortValue, sortValue: c.sortValue, render: c.render })),
    {
      accessor: 'is_active',
      title: tr('نشط', 'Active'),
      width: 80,
      sortable: true,
      render: (r) => {
        const locked = lockedRow?.(r);
        return (
          <Tooltip label={locked} disabled={!locked}>
            <Box w="fit-content" onClick={(e) => e.stopPropagation()}>
              <Switch size="xs" checked={r.is_active} disabled={!!locked || toggle.isPending} onChange={() => toggle.mutate(r)} aria-label={tr('نشط', 'Active')} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'actions',
      title: '',
      width: rowActions ? 92 : 56,
      render: (r) => (
        <Group gap={2} justify="flex-end" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          {rowActions?.(r)}
          <Tooltip label={tr('تعديل', 'Edit')}><ActionIcon onClick={() => open(r)} aria-label={tr('تعديل', 'Edit')}><IconPencil size={16} /></ActionIcon></Tooltip>
        </Group>
      ),
    },
  ];

  return (
    <Panel flush title={title} description={description}>
      <Toolbar end={<Button leftSection={<IconPlus size={16} />} onClick={() => open()}>{addLabel}</Button>}>
        <SearchInput value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder={tr(`ابحث في ${all.length} عنصر…`, `Search ${all.length} items…`)} />
        {filter && (
          <Select w={{ base: '100%', sm: 200 }} searchable value={filterValue} onChange={(v) => setFilterValue(v ?? '')}
            data={[{ value: '', label: `${filter.label}: ${tr('الكل', 'All')}` }, ...(filter.options ?? []).map((o) => ({ value: String(o.value), label: o.label }))]} />
        )}
        <Select w={{ base: '48%', sm: 150 }} value={state} allowDeselect={false} onChange={(v) => setState((v ?? 'all') as typeof state)}
          data={[
            { value: 'all', label: tr(`الكل (${all.length})`, `All (${all.length})`) },
            { value: 'active', label: tr(`النشطة (${activeCount})`, `Active (${activeCount})`) },
            { value: 'inactive', label: tr(`الموقوفة (${all.length - activeCount})`, `Inactive (${all.length - activeCount})`) },
          ]} />
        {missingCount > 0 && (
          <Chip checked={onlyMissing} onChange={setOnlyMissing} color="orange" variant="light" size="sm" icon={<IconAlertCircle size={14} />}>
            {tr(`${missingCount} بدون إنجليزي`, `${missingCount} missing English`)}
          </Chip>
        )}
      </Toolbar>
      {list.isError ? (
        <Box p="md"><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box>
      ) : (
        <ClientTable<LookupRow>
          records={items}
          columns={columns}
          idAccessor="id"
          fetching={list.isLoading}
          minHeight={items.length ? undefined : 240}
          onRowClick={({ record }) => open(record)}
          rowStyle={(r) => ({ cursor: 'pointer', opacity: r.is_active ? 1 : 0.55 })}
          noRecordsText={q || onlyMissing || state !== 'all' ? tr('لا توجد نتائج مطابقة', 'No matching results') : tr('لا توجد عناصر بعد', 'Nothing here yet')}
        />
      )}

      <Modal opened={!!draft} onClose={() => setDraft(null)} size={520} title={draft?.id ? tr('تعديل', 'Edit') : addLabel}>
        <BilingualScope key={draft?.id ?? 'new'}>
          {draft && (
            <LookupForm draft={draft} onSubmit={() => save.mutate(draft)}>
              <BilingualField label={tr('الاسم', 'Name')} value={draft.name} onChange={(name) => setDraft({ ...draft, name })} errorAr={errors.nameAr} errorEn={errors.nameEn} data-autofocus />
              <TextInput
                label={tr('الكود', 'Code')}
                withAsterisk={!draft.id}
                value={draft.code}
                disabled={!!draft.id}
                dir="ltr"
                ff="monospace"
                onChange={(e) => setDraft({ ...draft, code: e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                error={errors.code}
                description={draft.id ? tr('ثابت — شروط النموذج تعتمد عليه', 'Fixed — form conditions depend on it') : tr('أحرف لاتينية كبيرة وأرقام و _ — مثال: VILLA_ATTACHED', 'Uppercase Latin letters, digits and _ — e.g. VILLA_ATTACHED')}
              />
              {extraFields.map((f) => {
                const locked = !!draft.id && f.createOnly;
                const desc = locked ? tr('لا يمكن تغييره بعد الإنشاء', 'Cannot be changed after creation') : f.description;
                return f.options ? (
                  <Select
                    key={f.key}
                    label={f.label}
                    description={desc}
                    withAsterisk={f.required}
                    searchable={f.options.length > 8}
                    clearable={!f.required}
                    value={draft[f.key] === '' || draft[f.key] == null ? null : String(draft[f.key])}
                    disabled={locked}
                    onChange={(v) => setDraft({ ...draft, [f.key]: v ?? '' })}
                    error={errors[f.key]}
                    placeholder={f.required ? tr('اختر…', 'Choose…') : '—'}
                    data={f.options.map((o) => ({ value: String(o.value), label: o.label }))}
                  />
                ) : (
                  <TextInput key={f.key} label={f.label} description={desc} withAsterisk={f.required} value={String(draft[f.key] ?? '')} disabled={locked}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.currentTarget.value })} error={errors[f.key]} />
                );
              })}
              <Group gap="lg" align="flex-end">
                <NumberInput w={110} label={tr('الترتيب', 'Order')} value={draft.sortOrder} allowDecimal={false} dir="ltr" onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })} />
                <Switch mb={8} checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.currentTarget.checked })} label={tr('نشط — يظهر في النموذج', 'Active — shown in the form')} />
              </Group>
              <Group justify="flex-end" gap="xs" mt="xs">
                <Button variant="default" onClick={() => setDraft(null)}>{tr('إلغاء', 'Cancel')}</Button>
                <Button type="submit" loading={save.isPending}>{tr('حفظ', 'Save')}</Button>
              </Group>
            </LookupForm>
          )}
        </BilingualScope>
      </Modal>
    </Panel>
  );
}

/** The dialog form: shows the translation status and, on save, jumps to the missing language. */
function LookupForm({ draft, onSubmit, children }: { draft: Draft; onSubmit: () => void; children: ReactNode }) {
  const scope = useBilingualScope();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const miss = missingLang(draft.name);
        if (miss) return scope?.setLang(miss);
        onSubmit();
      }}
    >
      <Stack gap="md">
        <BilingualToolbar values={[{ value: draft.name }]} />
        {children}
      </Stack>
    </form>
  );
}
